import React from 'react';
import { Pencil, Shuffle, Users } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { getGroupLabel } from '../generators/groupLabels';

const ParticipantRow = ({ participant, groupIndex, groupCount, formatId, groupNames, onMove, theme }) => (
  <div className={`flex items-center gap-2 rounded-lg px-2.5 py-1.5 ${theme.backgroundSecondary}`}>
    <span className={`flex min-w-0 flex-1 items-center gap-1.5 truncate text-xs ${theme.text}`}>
      {participant.seed && (
        <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded text-[10px] font-bold ${theme.card} ${theme.textMuted}`}>
          {participant.seed}
        </span>
      )}
      <span className="truncate" title={participant.name}>{participant.name}</span>
    </span>
    <select
      value={groupIndex}
      onChange={(e) => onMove(participant.id, Number(e.target.value))}
      className={`w-32 shrink-0 truncate rounded border px-1.5 py-1 text-[11px] ${theme.input}`}
    >
      {Array.from({ length: groupCount }, (_, i) => (
        <option key={i} value={i}>
          {getGroupLabel(formatId, i, groupNames)}
        </option>
      ))}
    </select>
  </div>
);

/**
 * Lets the organizer see and edit exactly which participant lands in which
 * group/region before generating — instead of the serpentine split just
 * happening invisibly. "Randomize" re-shuffles from scratch; each row's
 * dropdown moves that one participant to a different group directly (no
 * drag-and-drop — a plain select is more robust and just as fast here).
 *
 * Each group's own name is editable too (`groupNames`/`onRename`) — e.g.
 * renaming "Group A"/"Group B" to real club names for a Dual Meet
 * ("Parsippany" vs "Livingston"). Ports the same rename capability the
 * legacy production scheduler already has for its MLP cross-group ("Dual
 * Match") structure — see groupLabels.js's getGroupLabel for where the
 * override actually takes effect. `maxLength` matches
 * tournament_rounds.group_name's VarChar(50) column so a saved schedule's
 * name can never get silently truncated by the backend.
 */
const GroupAssignmentStep = ({ format, groups, groupNames, onRename, onMove, onRandomize }) => {
  const { theme } = useTheme();
  if (!groups) return null;

  const unitLabel = format.groupUnitLabel || 'Group';

  return (
    <div className={`rounded-xl border ${theme.border} ${theme.backgroundSecondary} p-4`}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className={`flex items-center gap-2 text-sm font-semibold ${theme.text}`}>
          <Users size={16} />
          {unitLabel} assignment
        </div>
        <button
          type="button"
          onClick={onRandomize}
          className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${theme.buttonGhost}`}
        >
          <Shuffle size={13} />
          Randomize
        </button>
      </div>

      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {groups.map((groupParticipants, groupIndex) => (
          <div key={groupIndex} className={`rounded-lg border ${theme.border} ${theme.card} p-3`}>
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className={`relative min-w-0 flex-1 rounded border ${theme.border} ${theme.backgroundSecondary} transition-colors hover:border-current focus-within:border-current`}>
                <input
                  type="text"
                  value={groupNames?.[groupIndex] ?? ''}
                  onChange={(e) => onRename(groupIndex, e.target.value)}
                  placeholder={getGroupLabel(format.id, groupIndex)}
                  maxLength={50}
                  aria-label={`${unitLabel} ${groupIndex + 1} name — click to rename`}
                  title="Click to rename"
                  className={`w-full truncate rounded bg-transparent py-0.5 pl-1.5 pr-5 text-xs font-bold ${theme.text} placeholder:font-bold placeholder:opacity-70 focus:outline-none focus:ring-1 focus:ring-current`}
                />
                <Pencil size={10} className={`pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 opacity-50 ${theme.textMuted}`} />
              </div>
              <span className={`shrink-0 text-[11px] ${theme.textMuted}`}>{groupParticipants.length}</span>
            </div>
            <div className="max-h-64 space-y-1 overflow-y-auto pr-1">
              {groupParticipants.map((participant) => (
                <ParticipantRow
                  key={participant.id}
                  participant={participant}
                  groupIndex={groupIndex}
                  groupCount={groups.length}
                  formatId={format.id}
                  groupNames={groupNames}
                  onMove={onMove}
                  theme={theme}
                />
              ))}
              {groupParticipants.length === 0 && (
                <div className={`px-2 py-3 text-center text-[11px] ${theme.textMuted}`}>Empty</div>
              )}
            </div>
          </div>
        ))}
      </div>
      <p className={`mt-3 text-xs ${theme.textMuted}`}>
        Defaults to a balanced seed-based split. Move anyone with the dropdown, or randomize the whole split.
      </p>
    </div>
  );
};

export default GroupAssignmentStep;
